/**
 * <scroll-progress-bar>
 * renders a scrollbar tracking scroll position of arbitrary element
 *
 * Example usage:
 * <scroll-progress-bar data-target="#SliderGallery"></scroll-progress-bar>
 */

class ScrollProgressBar extends HTMLElement {
  constructor() {
    super();
    this.retryCount = 0;
    this.retryIntervalMs = 500;
    this.maxRetries = 15;
    this.isWatching = false;
    this.targetEl = null;
    this.hidden = true; // hide until scroll established
  }

  connectedCallback() {
    this.initScrollDetection();
  }

  disconnectedCallback() {
    this.isWatching = false;
    clearTimeout(this.retryTimeout);
    if (this.handleScroll) this.targetEl?.removeEventListener('scroll', this.handleScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /**
   * Looks for target el in DOM. If found, starts watching scroll. If not, retries
   * X times (waiting for targets injected in DOM clientside).
   */
  initScrollDetection() {
    if (this.isWatching || !this.dataset.target) return;

    this.targetEl = document.querySelector(this.dataset.target);

    if (this.targetEl) {
      this.setupScrollWatch();
    } else if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.retryTimeout = setTimeout(() => this.initScrollDetection(), this.retryIntervalMs);
    }
  }

  /**
   * Watches target for scroll events, updates progress once per frame,
   * and recalculates when resized.
   */
  setupScrollWatch() {
    if (!this.targetEl) return;

    this.isWatching = true;

    let ticking = false; // max update once per frame
    this.handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.updateProgress();
        ticking = false;
      });
    };
    this.targetEl.addEventListener('scroll', this.handleScroll, { passive: true });

    this.updateProgress(); // initial paint

    // Recalculate when target is resized.
    // Defer layout work so ResizeObserver doesn't report undelivered notifications.
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.updateProgress());
    });
    this.resizeObserver.observe(this.targetEl);
  }

  /**
   * Calculates scroll progress percentage based on element dimensions.
   * Handles either horizontal or vertical scrolling (horizontal wins if both active)
   * Updates value on `--progress` CSS custom property
   */
  updateProgress() {
    if (!this.targetEl) {
      this.hidden = true;
      return;
    }

    let progress = 0;

    // Horizontal scroll
    if (this.targetEl.scrollWidth > this.targetEl.clientWidth) {
      const furthestVisiblePoint = this.targetEl.scrollLeft + this.targetEl.clientWidth;
      progress = (furthestVisiblePoint / this.targetEl.scrollWidth) * 100;
    // Vertical scroll
    } else if (this.targetEl.scrollHeight > this.targetEl.clientHeight) {
      const furthestVisiblePoint = this.targetEl.scrollTop + this.targetEl.clientHeight;
      progress = (furthestVisiblePoint / this.targetEl.scrollHeight) * 100;
    // No scroll
    } else {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.style.setProperty('--progress', `${progress}%`);
  }
}

customElements.define('scroll-progress-bar', ScrollProgressBar);
